import { $, $$, debounce } from '../utils/helpers.js';

/**
 * [DSS] Search Popup - Highend Fuzzy Search
 *
 * Fullscreen-Popup-Suche mit:
 * - Echtzeit Predictive Search ueber Shopify API
 * - Client-seitigem Fuzzy Matching und Highlighting
 * - Keyboard-Navigation (Pfeiltasten, Enter, ESC)
 * - Body-Scroll-Lock bei geoeffnetem Popup
 * - AbortController fuer abbrechbare Requests
 */
export class SearchPopup {
  constructor() {
    this.popup = $('[data-search-popup]');
    if (!this.popup) return;

    this.trigger = $('[data-search-popup-trigger]', this.popup);
    this.overlay = $('[data-search-popup-overlay]', this.popup);
    this.backdrop = $('[data-search-popup-backdrop]', this.popup);
    this.closeBtn = $('[data-search-popup-close]', this.popup);
    this.input = $('[data-search-popup-input]', this.popup);
    this.resetBtn = $('[data-search-popup-reset]', this.popup);
    this.results = $('[data-search-popup-results]', this.popup);
    this.status = $('[data-search-popup-status]', this.popup);

    this.isOpen = false;
    this.searchTerm = '';
    this.abortController = null;
    this.highlightedIndex = -1;
    this.resultItems = [];
    this.predictiveSearchUrl = window.routes?.predictive_search_url || '/search/suggest';
    this.maxResults = parseInt(this.popup.dataset.maxResults) || 10;

    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    this.trigger.addEventListener('click', () => this.open());
    this.closeBtn.addEventListener('click', () => this.close());
    this.backdrop.addEventListener('click', () => this.close());

    this.input.addEventListener(
      'input',
      debounce(() => this.onInput(), 250)
    );

    this.input.addEventListener('keydown', (e) => this.onKeydown(e));

    this.resetBtn.addEventListener('click', () => {
      this.input.value = '';
      this.input.focus();
      this.resetBtn.classList.add('hidden');
      this.clearResults();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    document.addEventListener('click', (e) => {
      if (this.isOpen && !this.popup.contains(e.target)) {
        this.close();
      }
    });
  }

  open() {
    if (this.isOpen) return;

    this.isOpen = true;
    this.overlay.classList.add('is-open');
    document.body.classList.add('dss-search-popup--open');

    this.highlightedIndex = -1;
    this.clearResults();
    this.showLoading();

    this.loadInitialProducts();

    requestAnimationFrame(() => {
      setTimeout(() => this.input.focus(), 350);
    });
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.overlay.classList.remove('is-open');
    document.body.classList.remove('dss-search-popup--open');

    this.input.value = '';
    this.resetBtn.classList.add('hidden');
    this.searchTerm = '';
    this.abortFetch();
    this.clearResults();
  }

  onInput() {
    const value = this.input.value.trim();

    if (value === this.searchTerm) return;
    this.searchTerm = value;

    if (value.length > 0) {
      this.resetBtn.classList.remove('hidden');
    } else {
      this.resetBtn.classList.add('hidden');
    }

    this.abortFetch();

    if (value.length < 1) {
      this.clearResults();
      this.loadInitialProducts();
      return;
    }

    this.performSearch(value);
  }

  abortFetch() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  async performSearch(query) {
    this.showLoading();

    this.abortController = new AbortController();

    try {
      const url = `${this.predictiveSearchUrl}?q=${encodeURIComponent(query)}&section_id=predictive-search&resources[limit]=20`;
      const response = await fetch(url, {
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }

      const html = await response.text();
      const parsed = this.parseResults(html);

      if (parsed && this.hasResults(parsed)) {
        this.renderResults(parsed, query);
      } else {
        this.renderNoResults(query);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Search error:', error);
      this.clearResults();
    }
  }

  parseResults(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const predictiveSearch = doc.querySelector('#predictive-search-results');
    if (!predictiveSearch) return null;

    return {
      queries: this.extractListItems(predictiveSearch, 'queries'),
      collections: this.extractListItems(predictiveSearch, 'collection'),
      pages: this.extractListItems(predictiveSearch, 'page'),
      articles: this.extractListItems(predictiveSearch, 'article'),
      products: this.extractProductItems(predictiveSearch),
    };
  }

  extractListItems(container, type) {
    const items = [];
    const listItems = container.querySelectorAll(`[id*="predictive-search-option-${type}-"]`);

    listItems.forEach((item) => {
      const link = item.querySelector('a');
      const heading = item.querySelector('.predictive-search__item-heading');

      if (link && heading) {
        items.push({
          title: heading.textContent.trim(),
          url: link.getAttribute('href'),
        });
      }
    });

    return items;
  }

  extractProductItems(container) {
    const items = [];
    const listItems = container.querySelectorAll('[id*="predictive-search-option-product-"]');

    listItems.forEach((item) => {
      const link = item.querySelector('a');
      const heading = item.querySelector('.predictive-search__item-heading');
      const vendor = item.querySelector('.predictive-search__item-vendor');
      const image = item.querySelector('.predictive-search__image');
      const price = item.querySelector('.price');

      if (link && heading) {
        items.push({
          title: heading.textContent.trim(),
          url: link.getAttribute('href'),
          vendor: vendor ? vendor.textContent.trim() : null,
          imageSrc: image ? image.getAttribute('src') : null,
          imageAlt: image ? image.getAttribute('alt') : '',
          priceHtml: price ? price.outerHTML : null,
        });
      }
    });

    return items;
  }

  hasResults(parsed) {
    return (
      parsed.queries.length > 0 ||
      parsed.collections.length > 0 ||
      parsed.pages.length > 0 ||
      parsed.articles.length > 0 ||
      parsed.products.length > 0
    );
  }

  renderResults(data, query) {
    const normalize = (str) =>
      str
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

    const scoreResult = (title) => {
      const normTitle = normalize(title);
      const normQuery = normalize(query);

      if (normTitle === normQuery) return 100;
      if (normTitle.startsWith(normQuery)) return 80;
      if (normTitle.includes(normQuery)) return 60;

      const words = normTitle.split(/\s+/);
      if (words.some((w) => w.startsWith(normQuery))) return 40;
      if (words.some((w) => w.includes(normQuery))) return 20;

      return this.fuzzyScore(normTitle, normQuery);
    };

    const sortAndLimit = (items, max = 3) => {
      return items
        .map((item) => ({
          ...item,
          _score: scoreResult(item.title),
        }))
        .sort((a, b) => b._score - a._score)
        .slice(0, max);
    };

    const highlightText = (text) => {
      if (!query) return this.escapeHtml(text);
      const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedQuery})`, 'gi');
      return this.escapeHtml(text).replace(
        regex,
        '<mark>$1</mark>'
      );
    };

    let html = '';

    const showCollection = this.popup.dataset.showCollections !== 'false';
    const showPages = this.popup.dataset.showPages !== 'false';
    const showArticles = this.popup.dataset.showArticles !== 'false';
    const showImages = this.popup.dataset.showImages !== 'false';

    if (data.queries.length > 0) {
      const queries = sortAndLimit(data.queries, 3);
      html += this.renderGroup(
        this.getTranslation('suggestions', 'Vorschläge'),
        queries.map((q) => ({
          ...q,
          type: 'query',
          badge: this.getTranslation('search', 'Suche'),
        })),
        query,
        highlightText,
        showImages,
        'text'
      );
    }

    if (showCollection && data.collections.length > 0) {
      const collections = sortAndLimit(data.collections, 4);
      html += this.renderGroup(
        this.getTranslation('collections', 'Kollektionen'),
        collections.map((c) => ({
          ...c,
          type: 'collection',
          badge: this.getTranslation('collection', 'Kollekt.'),
        })),
        query,
        highlightText,
        showImages,
        'text'
      );
    }

    if (showPages && data.pages.length > 0) {
      const pages = sortAndLimit(data.pages, 3);
      html += this.renderGroup(
        this.getTranslation('pages', 'Seiten'),
        pages.map((p) => ({
          ...p,
          type: 'page',
          badge: this.getTranslation('page', 'Seite'),
        })),
        query,
        highlightText,
        showImages,
        'text'
      );
    }

    if (showArticles && data.articles.length > 0) {
      const articles = sortAndLimit(data.articles, 3);
      html += this.renderGroup(
        this.getTranslation('articles', 'Artikel'),
        articles.map((a) => ({
          ...a,
          type: 'article',
          badge: this.getTranslation('article', 'Artikel'),
        })),
        query,
        highlightText,
        showImages,
        'text'
      );
    }

    if (data.products.length > 0) {
      const products = sortAndLimit(data.products, this.maxResults);
      html += this.renderGroup(
        this.getTranslation('products', 'Produkte'),
        products,
        query,
        highlightText,
        showImages,
        'product'
      );
    }

    html += this.renderSearchAllButton(query);

    this.results.innerHTML = html;
    this.resultItems = $$('.dss-search-popup__result-link', this.results);

    if (this.resultItems.length > 0) {
      this.highlightedIndex = -1;
    }

    this.input.setAttribute('aria-expanded', 'true');
  }

  renderGroup(heading, items, query, highlightText, showImages, type) {
    let html = `<div class="dss-search-popup__result-group">`;
    html += `<div class="dss-search-popup__result-heading">${this.escapeHtml(heading)}</div>`;
    html += `<ul class="dss-search-popup__result-list" role="group">`;

    items.forEach((item, index) => {
      const title = highlightText(item.title);
      const badge = item.badge || '';
      const itemType = item.type || type;

      if (type === 'product' && item.imageSrc && showImages) {
        html += `
          <li class="dss-search-popup__result-item dss-search-popup__result-item--product" role="option" aria-selected="false">
            <a href="${item.url}" class="dss-search-popup__result-link" data-index="${index}">
              <div class="dss-search-popup__result-image-wrapper">
                <img class="dss-search-popup__result-image" src="${item.imageSrc}" alt="${this.escapeHtml(item.imageAlt || item.title)}" loading="lazy" width="52" height="52">
              </div>
              <div class="dss-search-popup__result-info">
                ${item.vendor ? `<div class="dss-search-popup__result-vendor">${this.escapeHtml(item.vendor)}</div>` : ''}
                <div class="dss-search-popup__result-title">${title}</div>
                ${item.priceHtml ? `<div class="dss-search-popup__result-price">${item.priceHtml}</div>` : ''}
              </div>
            </a>
          </li>`;
      } else {
        html += `
          <li class="dss-search-popup__result-item dss-search-popup__result-item--text" role="option" aria-selected="false">
            <a href="${item.url}" class="dss-search-popup__result-link" data-index="${index}">
              ${badge ? `<span class="dss-search-popup__result-type-badge dss-search-popup__result-type-badge--${itemType}">${this.escapeHtml(badge)}</span>` : ''}
              <span class="dss-search-popup__result-title">${title}</span>
            </a>
          </li>`;
      }
    });

    html += `</ul></div>`;
    return html;
  }

  renderSearchAllButton(query) {
    const label = this.getTranslation('search_for', 'Alle Ergebnisse für') + ' „' + this.escapeHtml(query) + '“';
    return `
      <form action="${window.routes?.search_url || '/search'}" method="get" role="search">
        <input type="hidden" name="q" value="${this.escapeHtml(query)}">
        <input type="hidden" name="options[prefix]" value="last">
        <button type="submit" class="dss-search-popup__search-all">
          ${label}
          <svg viewBox="0 0 14 10" fill="none" aria-hidden="true" focusable="false"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.537.808a.5.5 0 01.817-.162l4 4a.5.5 0 010 .708l-4 4a.5.5 0 11-.708-.708L11.793 5.5H1a.5.5 0 010-1h10.793L8.646 1.354a.5.5 0 01-.109-.546z" fill="currentColor"></path></svg>
        </button>
      </form>`;
  }

  renderNoResults(query) {
    this.results.innerHTML = `
      <div class="dss-search-popup__empty">
        <span class="dss-search-popup__empty-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
        </span>
        <p class="dss-search-popup__empty-text">${this.getTranslation('no_results', 'Keine Ergebnisse für')} <strong>„${this.escapeHtml(query)}“</strong></p>
      </div>
    `;
  }

  showLoading() {
    this.results.innerHTML = `
      <div class="dss-search-popup__loading-state">
        <div class="loading__spinner">
          <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
            <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
          </svg>
        </div>
      </div>
    `;
  }

  showInitialState() {
    this.results.innerHTML = `
      <div class="dss-search-popup__initial">
        <span class="dss-search-popup__initial-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
        </span>
        <p class="dss-search-popup__initial-text">${this.getTranslation('start_typing', 'Gib einen Suchbegriff ein ...')}</p>
      </div>
    `;
  }

  async loadInitialProducts() {
    try {
      const response = await fetch('/collections/all/products.json?limit=80&sort_by=price-descending');

      if (!response.ok) {
        throw new Error(`Failed to load: ${response.status}`);
      }

      const data = await response.json();

      if (data.products && data.products.length > 0) {
        const shuffled = data.products.sort(() => Math.random() - 0.5);
        const selected = shuffled.slice(0, 6);
        this.renderInitialProducts(selected);
      } else {
        this.showInitialState();
      }
    } catch (error) {
      console.error('SearchPopup: Could not load initial products:', error);
      this.showInitialState();
    }
  }

  renderInitialProducts(products) {
    const heading = this.getTranslation('premium', 'Highlights');

    let html = `<div class="dss-search-popup__result-group">`;
    html += `<div class="dss-search-popup__result-heading">${this.escapeHtml(heading)}</div>`;
    html += `<div class="dss-search-popup__tile-grid" role="group">`;

    products.forEach((product, index) => {
      const imageSrc = this.getProductImage(product);
      const imageAlt = product.title;
      const priceData = this.getProductPrice(product);

      let priceHtml = '';
      if (priceData.price !== null) {
        const formattedPrice = new Intl.NumberFormat('de-DE', {
          style: 'currency',
          currency: 'EUR',
        }).format(priceData.price);

        if (priceData.compareAtPrice && priceData.compareAtPrice > priceData.price) {
          const formattedCompare = new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
          }).format(priceData.compareAtPrice);
          priceHtml = `
            <div class="dss-search-popup__tile-price">
              <span class="price-item price-item--sale">${formattedPrice}</span>
              <s class="price-item price-item--regular">${formattedCompare}</s>
            </div>`;
        } else {
          priceHtml = `
            <div class="dss-search-popup__tile-price">
              <span class="price-item">${formattedPrice}</span>
            </div>`;
        }
      }

      html += `
        <a href="/products/${this.escapeHtml(product.handle)}" class="dss-search-popup__tile" role="option" aria-selected="false" data-index="${index}">
          ${imageSrc ? `
          <div class="dss-search-popup__tile-image-wrapper">
            <img class="dss-search-popup__tile-image" src="${imageSrc}" alt="${this.escapeHtml(imageAlt)}" loading="lazy">
          </div>` : `
          <div class="dss-search-popup__tile-image-wrapper dss-search-popup__tile-image-wrapper--placeholder">
            <span class="svg-wrapper">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>
            </span>
          </div>`}
          <div class="dss-search-popup__tile-info">
            <div class="dss-search-popup__tile-title">${this.escapeHtml(product.title)}</div>
            ${priceHtml}
          </div>
        </a>`;
    });

    html += `</div></div>`;
    this.results.innerHTML = html;
    this.resultItems = $$('.dss-search-popup__tile', this.results);
    this.highlightedIndex = -1;
  }

  getProductImage(product) {
    if (product.featured_image && typeof product.featured_image === 'string') {
      return this.resizeShopifyImage(product.featured_image, '300x');
    }
    if (product.image && typeof product.image === 'string') {
      return this.resizeShopifyImage(product.image, '300x');
    }
    if (product.images && product.images.length > 0 && product.images[0].src) {
      return this.resizeShopifyImage(product.images[0].src, '300x');
    }
    return null;
  }

  resizeShopifyImage(url, size) {
    if (!url) return null;
    return url.replace(/(\.[a-z]{3,4})(\?|$)/, '_' + size + '$1$2');
  }

  getProductPrice(product) {
    let price = null;
    let compareAtPrice = null;

    if (product.variants && product.variants.length > 0 && product.variants[0].price) {
      price = parseFloat(product.variants[0].price);
    }
    if (!price && product.price) {
      price = parseFloat(product.price);
    }
    if (!price && product.price_min) {
      price = parseFloat(product.price_min);
    }

    if (product.variants && product.variants.length > 0 && product.variants[0].compare_at_price) {
      compareAtPrice = parseFloat(product.variants[0].compare_at_price);
    }
    if (!compareAtPrice && product.compare_at_price) {
      compareAtPrice = parseFloat(product.compare_at_price);
    }
    if (!compareAtPrice && product.compare_at_price_min) {
      compareAtPrice = parseFloat(product.compare_at_price_min);
    }

    return { price, compareAtPrice };
  }

  clearResults() {
    this.results.innerHTML = '';
    this.resultItems = [];
    this.highlightedIndex = -1;
    this.input.setAttribute('aria-expanded', 'false');
  }

  fuzzyScore(str, query) {
    let score = 0;
    let queryIdx = 0;

    for (let i = 0; i < str.length && queryIdx < query.length; i++) {
      if (str[i] === query[queryIdx]) {
        score += 1;
        queryIdx++;
        if (i > 0 && str[i - 1] === ' ') {
          score += 2;
        }
      }
    }

    if (queryIdx === query.length) {
      const ratio = score / str.length;
      return Math.round(ratio * 15);
    }

    return 0;
  }

  onKeydown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.moveHighlight(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.moveHighlight(-1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      this.selectHighlighted();
    }
  }

  moveHighlight(direction) {
    if (this.resultItems.length === 0) return;

    if (this.highlightedIndex >= 0) {
      this.resultItems[this.highlightedIndex].classList.remove('is-highlighted');
    }

    this.highlightedIndex += direction;

    if (this.highlightedIndex < 0) {
      this.highlightedIndex = this.resultItems.length - 1;
    } else if (this.highlightedIndex >= this.resultItems.length) {
      this.highlightedIndex = 0;
    }

    const item = this.resultItems[this.highlightedIndex];
    item.classList.add('is-highlighted');
    item.scrollIntoView({ block: 'nearest' });
  }

  selectHighlighted() {
    if (this.highlightedIndex >= 0 && this.resultItems[this.highlightedIndex]) {
      const link = this.resultItems[this.highlightedIndex];
      if (link.href) {
        window.location.href = link.href;
      }
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  getTranslation(key, fallback) {
    const translations = {
      suggestions: 'Vorschläge',
      collections: 'Kollektionen',
      collection: 'Kollekt.',
      pages: 'Seiten',
      page: 'Seite',
      articles: 'Artikel',
      article: 'Artikel',
      products: 'Produkte',
      premium: 'Highlights',
      search: 'Suche',
      search_for: 'Alle Ergebnisse für',
      no_results: 'Keine Ergebnisse für',
      start_typing: 'Gib einen Suchbegriff ein ...',
    };
    return translations[key] || fallback;
  }
}
